/**
 * FilePreview — Preview component for non-image, non-audio file attachments.
 *
 * - PDFs: Inline iframe via authenticated blob URL
 * - Videos: <video> tag with auth blob URL
 * - Other files: Download card with icon, filename, size, download button
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { FileText, FileArchive, File, Download, Loader2 } from "lucide-react";
import { ensureValidToken, getAccessToken } from "../lib/api";
import styles from "./FilePreview.module.css";

const BASE_URL = "/api/v1";

interface FilePreviewProps {
  fileId: string;
  contentType: string;
  filename: string;
  encryptionKey?: string;
  encryptionIv?: string;
}

/** Human-readable file size */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Get the appropriate Lucide icon for a given MIME type */
function getFileIcon(contentType: string) {
  if (contentType.includes("zip") || contentType.includes("gzip") || contentType.includes("tar") || contentType.includes("rar") || contentType.includes("7z")) {
    return FileArchive;
  }
  if (
    contentType.includes("pdf") ||
    contentType.includes("word") ||
    contentType.includes("document") ||
    contentType.includes("spreadsheet") ||
    contentType.includes("excel") ||
    contentType.includes("text/") ||
    contentType.includes("csv")
  ) {
    return FileText;
  }
  return File;
}

/** Get a color class based on file type */
function getIconColorClass(contentType: string): string {
  if (contentType === "application/pdf") return styles.iconPdf;
  if (contentType.includes("zip") || contentType.includes("gzip") || contentType.includes("tar")) return styles.iconArchive;
  if (contentType.includes("word") || contentType.includes("document")) return styles.iconDoc;
  if (contentType.includes("spreadsheet") || contentType.includes("excel")) return styles.iconSpreadsheet;
  if (contentType.startsWith("video/")) return styles.iconVideo;
  return styles.iconDefault;
}

export function FilePreview({ fileId, contentType, filename, encryptionKey, encryptionIv }: FilePreviewProps) {
  const isPdf = contentType === "application/pdf";
  const isVideo = contentType.startsWith("video/");

  if (isPdf) {
    return <PdfPreview fileId={fileId} filename={filename} encryptionKey={encryptionKey} encryptionIv={encryptionIv} />;
  }

  if (isVideo) {
    return <VideoPreview fileId={fileId} filename={filename} contentType={contentType} encryptionKey={encryptionKey} encryptionIv={encryptionIv} />;
  }

  return <FileCard fileId={fileId} filename={filename} contentType={contentType} encryptionKey={encryptionKey} encryptionIv={encryptionIv} />;
}

// ── PDF Preview ──

function PdfPreview({ fileId, filename, encryptionKey, encryptionIv }: { fileId: string; filename: string; encryptionKey?: string; encryptionIv?: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;

    async function load() {
      try {
        await ensureValidToken();
        const token = getAccessToken();
        const res = await fetch(`${BASE_URL}/files/${fileId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        if (encryptionKey && encryptionIv) {
          const { fetchAndDecryptFile } = await import("../lib/file-crypto");
          url = await fetchAndDecryptFile(fileId, "application/pdf", encryptionKey, encryptionIv);
        } else {
          const blob = await res.blob();
          const typedBlob = new Blob([blob], { type: "application/pdf" });
          url = URL.createObjectURL(typedBlob);
        }
        if (!cancelled) {
          setBlobUrl(url);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);

  const handleDownload = useCallback(async () => {
    try {
      let url: string;
      if (encryptionKey && encryptionIv) {
        const { fetchAndDecryptFile } = await import("../lib/file-crypto");
        url = await fetchAndDecryptFile(fileId, "application/pdf", encryptionKey, encryptionIv);
      } else {
        await ensureValidToken();
        const token = getAccessToken();
        const res = await fetch(`${BASE_URL}/files/${fileId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // best effort
    }
  }, [fileId, filename]);

  if (loading) {
    return (
      <div className={styles.pdfContainer}>
        <div className={styles.loadingState}>
          <Loader2 size={24} className="spin" />
          <span>PDF wird geladen…</span>
        </div>
      </div>
    );
  }

  if (error || !blobUrl) {
    return (
      <div className={styles.fileCard}>
        <div className={`${styles.fileIcon} ${styles.iconPdf}`}>
          <FileText size={24} />
        </div>
        <div className={styles.fileInfo}>
          <span className={styles.fileName}>{filename}</span>
          <span className={styles.fileType}>PDF</span>
        </div>
        <button className={styles.downloadBtn} onClick={handleDownload} title="Herunterladen">
          <Download size={16} />
          <span>Download</span>
        </button>
      </div>
    );
  }

  return (
    <div className={styles.pdfContainer}>
      <iframe
        src={blobUrl}
        title={filename}
        className={styles.pdfIframe}
      />
      <div className={styles.pdfFooter}>
        <span className={styles.pdfFilename}>{filename}</span>
        <button className={styles.downloadBtn} onClick={handleDownload} title="Herunterladen">
          <Download size={14} />
          <span>Download</span>
        </button>
      </div>
    </div>
  );
}

// ── Video Preview ──

function VideoPreview({ fileId, filename, contentType, encryptionKey, encryptionIv }: { fileId: string; filename: string; contentType: string; encryptionKey?: string; encryptionIv?: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;

    async function load() {
      try {
        await ensureValidToken();
        const token = getAccessToken();
        const res = await fetch(`${BASE_URL}/files/${fileId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        if (encryptionKey && encryptionIv) {
          const { fetchAndDecryptFile } = await import("../lib/file-crypto");
          url = await fetchAndDecryptFile(fileId, contentType, encryptionKey, encryptionIv);
        } else {
          const blob = await res.blob();
          const typedBlob = new Blob([blob], { type: contentType });
          url = URL.createObjectURL(typedBlob);
        }
        if (!cancelled) {
          setBlobUrl(url);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId, contentType]);

  if (loading) {
    return (
      <div className={styles.videoContainer}>
        <div className={styles.loadingState}>
          <Loader2 size={24} className="spin" />
          <span>Video wird geladen…</span>
        </div>
      </div>
    );
  }

  if (error || !blobUrl) {
    return <FileCard fileId={fileId} filename={filename} contentType={contentType} encryptionKey={encryptionKey} encryptionIv={encryptionIv} />;
  }

  return (
    <div className={styles.videoContainer}>
      <video
        src={blobUrl}
        controls
        className={styles.videoPlayer}
        preload="metadata"
      >
        Dein Browser unterstützt kein Video.
      </video>
    </div>
  );
}

// ── Generic File Card ──

function FileCard({ fileId, filename, contentType, encryptionKey, encryptionIv }: { fileId: string; filename: string; contentType: string; encryptionKey?: string; encryptionIv?: string }) {
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);
  const fetchedRef = useRef(false);

  // Fetch file size via HEAD request
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    async function fetchSize() {
      try {
        await ensureValidToken();
        const token = getAccessToken();
        const res = await fetch(`${BASE_URL}/files/${fileId}`, {
          method: "HEAD",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const length = res.headers.get("Content-Length");
          if (length) setFileSize(parseInt(length, 10));
        }
      } catch {
        // ignore — size is optional
      }
    }

    fetchSize();
  }, [fileId]);

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await ensureValidToken();
      const token = getAccessToken();
      let url: string;
      if (encryptionKey && encryptionIv) {
        const { fetchAndDecryptFile } = await import("../lib/file-crypto");
        url = await fetchAndDecryptFile(fileId, contentType, encryptionKey, encryptionIv);
      } else {
        const res = await fetch(`${BASE_URL}/files/${fileId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
      }
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // best effort
    } finally {
      setDownloading(false);
    }
  }, [fileId, filename, contentType, encryptionKey, encryptionIv, downloading]);

  const Icon = getFileIcon(contentType);
  const colorClass = getIconColorClass(contentType);

  return (
    <div className={styles.fileCard}>
      <div className={`${styles.fileIcon} ${colorClass}`}>
        <Icon size={24} />
      </div>
      <div className={styles.fileInfo}>
        <span className={styles.fileName}>{filename}</span>
        {fileSize !== null && (
          <span className={styles.fileSize}>{formatFileSize(fileSize)}</span>
        )}
      </div>
      <button
        className={styles.downloadBtn}
        onClick={handleDownload}
        disabled={downloading}
        title="Herunterladen"
      >
        {downloading ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
        <span>{downloading ? "…" : "Download"}</span>
      </button>
    </div>
  );
}
